import {
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
    Renderer2,
} from '@angular/core';
import { UilabMatIconModule } from '@uilab/ng-mat-components';
import { CssVariableSnippetComponent } from '../../../../shared/components/css-variable-snippet/css-variable-snippet.component';
import { StylingCardShellComponent } from '../styling-card-shell/styling-card-shell.component';

@Component({
    selector: 'avd-overlay-color-card',
    standalone: true,
    templateUrl: './overlay-color-card.component.html',
    styleUrls: ['./overlay-color-card.component.scss'],
    imports: [
        StylingCardShellComponent,
        UilabMatIconModule,
        CssVariableSnippetComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverlayColorCardComponent implements OnDestroy {
    @Input() cssVariable: string = '';
    @Input() label: string = '';
    private readonly renderer = inject(Renderer2);
    private readonly cdr = inject(ChangeDetectorRef);
    overlayPosition: number = 60;
    private isDragging: boolean = false;
    private removePointerMoveListener?: () => void;
    private removePointerUpListener?: () => void;
    private removePointerCancelListener?: () => void;
    private activeDragHandle?: HTMLElement;
    private activePointerId?: number;

    get cssVariableName(): string {
        return this.cssVariable.trim();
    }

    get overlayColor(): string {
        return this.cssVariableName;
    }

    get cssVariableSnippet(): string {
        return this.cssVariableName;
    }

    ngOnDestroy(): void {
        this.releasePointerCapture();
        this.removeDocumentDragListeners();
    }

    startOverlayDrag(event: PointerEvent, previewElement: HTMLElement): void {
        event.preventDefault();
        const handle = event.currentTarget as HTMLElement | null;

        if (!handle) {
            return;
        }

        this.releasePointerCapture();
        handle.setPointerCapture(event.pointerId);
        this.activeDragHandle = handle;
        this.activePointerId = event.pointerId;

        this.isDragging = true;
        this.removeDocumentDragListeners();
        this.updateOverlayPosition(event.clientX, previewElement);

        this.removePointerMoveListener = this.renderer.listen(
            handle,
            'pointermove',
            (moveEvent: PointerEvent) => {
                if (
                    !this.isDragging ||
                    moveEvent.pointerId !== this.activePointerId
                ) {
                    return;
                }

                this.updateOverlayPosition(moveEvent.clientX, previewElement);
            },
        );

        this.removePointerUpListener = this.renderer.listen(
            handle,
            'pointerup',
            (upEvent: PointerEvent) => {
                if (upEvent.pointerId !== this.activePointerId) {
                    return;
                }

                this.endOverlayDrag();
            },
        );

        this.removePointerCancelListener = this.renderer.listen(
            handle,
            'pointercancel',
            (cancelEvent: PointerEvent) => {
                if (cancelEvent.pointerId !== this.activePointerId) {
                    return;
                }

                this.endOverlayDrag();
            },
        );
    }

    private updateOverlayPosition(
        clientX: number,
        previewElement: HTMLElement,
    ): void {
        const rect = previewElement.getBoundingClientRect();

        if (rect.width <= 0) {
            return;
        }

        const relativeX = clientX - rect.left;
        const widthPercent = (relativeX / rect.width) * 100;
        this.overlayPosition = Math.min(100, Math.max(0, widthPercent));
        this.cdr.markForCheck();
    }

    private endOverlayDrag(): void {
        this.isDragging = false;
        this.releasePointerCapture();
        this.removeDocumentDragListeners();
    }

    private removeDocumentDragListeners(): void {
        if (this.removePointerMoveListener) {
            this.removePointerMoveListener();
            this.removePointerMoveListener = undefined;
        }

        if (this.removePointerUpListener) {
            this.removePointerUpListener();
            this.removePointerUpListener = undefined;
        }

        if (this.removePointerCancelListener) {
            this.removePointerCancelListener();
            this.removePointerCancelListener = undefined;
        }
    }

    private releasePointerCapture(): void {
        if (
            this.activeDragHandle &&
            this.activePointerId !== undefined &&
            this.activeDragHandle.hasPointerCapture(this.activePointerId)
        ) {
            this.activeDragHandle.releasePointerCapture(this.activePointerId);
        }

        this.activeDragHandle = undefined;
        this.activePointerId = undefined;
    }
}
